import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    MagnifyingGlassIcon,
    BeakerIcon
} from '@heroicons/react/24/outline';

interface UnitOfMeasure {
    id: string;
    name: string;
    abbreviation: string;
    unit_type: string;
    is_base_unit: boolean;
}

interface MasterIngredient {
    id: string;
    name: string;
    description?: string;
    base_unit_id: string;
    base_unit?: UnitOfMeasure;
    is_active: boolean;
    created_at: string;
    updated_at?: string;
}

interface Product {
    id: string;
    name: string;
    manufacturer?: string;
    master_ingredient_id?: string;
}

export default function MasterIngredientsPage() {
    const navigate = useNavigate();
  const params = useParams({ strict: false });
  const routerState = useRouterState();
    const [masterIngredients, setMasterIngredients] = useState<MasterIngredient[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [units, setUnits] = useState<UnitOfMeasure[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showInactiveOnly, setShowInactiveOnly] = useState(false);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [selectedIngredient, setSelectedIngredient] = useState<MasterIngredient | null>(null);
    const [productSearch, setProductSearch] = useState('');

    // Form states
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        base_unit_id: ''
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchData();
    }, [showInactiveOnly]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams();
            params.append('active_only', (!showInactiveOnly).toString());
            params.append('limit', '1000');

            const [ingredientsResponse, productsResponse, unitsResponse] = await Promise.all([
                apiFetch(`/api/inventory/master-ingredients/?${params.toString()}`),
                apiFetch('/api/products/?limit=100'),
                apiFetch('/api/units/')
            ]);

            if (ingredientsResponse.ok && productsResponse.ok && unitsResponse.ok) {
                const ingredientsData = await ingredientsResponse.json();
                const productsData = await productsResponse.json();
                const unitsData = await unitsResponse.json();

                setMasterIngredients(ingredientsData);
                setProducts(productsData.products || productsData);
                setUnits(unitsData);
            } else {
                throw new Error('Failed to fetch data');
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load master ingredients. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const filteredIngredients = masterIngredients.filter(ingredient =>
        ingredient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ingredient.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ingredient.base_unit?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ingredient.base_unit?.abbreviation.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            const response = await apiFetch('/api/inventory/master-ingredients/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                await fetchData();
                setShowCreateModal(false);
                setFormData({ name: '', description: '', base_unit_id: '' });
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to create master ingredient');
            }
        } catch (err) {
            console.error('Error creating master ingredient:', err);
            setError(err instanceof Error ? err.message : 'Failed to create master ingredient');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedIngredient) return;

        setSubmitting(true);
        setError(null);

        try {
            const response = await apiFetch(`/api/inventory/master-ingredients/${selectedIngredient.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                await fetchData();
                setShowEditModal(false);
                setSelectedIngredient(null);
                setFormData({ name: '', description: '', base_unit_id: '' });
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to update master ingredient');
            }
        } catch (err) {
            console.error('Error updating master ingredient:', err);
            setError(err instanceof Error ? err.message : 'Failed to update master ingredient');
        } finally {
            setSubmitting(false);
        }
    };

    const handleLinkProduct = async (productId: string, masterIngredientId: string) => {
        try {
            const response = await apiFetch(`/api/products/${productId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ master_ingredient_id: masterIngredientId || null }),
            });

            if (response.ok) {
                // Update the products state locally instead of refetching all data
                setProducts(prevProducts =>
                    prevProducts.map(product =>
                        product.id === productId
                            ? { ...product, master_ingredient_id: masterIngredientId || undefined }
                            : product
                    )
                );
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to link product');
            }
        } catch (err) {
            console.error('Error linking product:', err);
            setError(err instanceof Error ? err.message : 'Failed to link product');
        }
    };

    const openEditModal = (ingredient: MasterIngredient) => {
        setSelectedIngredient(ingredient);
        setFormData({
            name: ingredient.name,
            description: ingredient.description || '',
            base_unit_id: ingredient.base_unit_id
        });
        setShowEditModal(true);
    };

    const getLinkedProducts = (masterIngredientId: string) => {
        return products.filter(product => product.master_ingredient_id === masterIngredientId);
    };

    const getUnlinkedProducts = () => {
        return products.filter(product => {
            const isUnlinked = !product.master_ingredient_id;
            const matchesSearch = productSearch === '' ||
                product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
                (product.manufacturer && product.manufacturer.toLowerCase().includes(productSearch.toLowerCase()));
            return isUnlinked && matchesSearch;
        });
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Master Ingredients</h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Manage master ingredients and link products to them
                        </p>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        New Master Ingredient
                    </button>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">Error</h3>
                                <p className="mt-1 text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="bg-white shadow rounded-lg p-6">
                    <div className="flex items-center space-x-4">
                        <div className="flex-1">
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search master ingredients..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
                                />
                            </div>
                        </div>

                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={showInactiveOnly}
                                onChange={(e) => setShowInactiveOnly(e.target.checked)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">Show inactive only</span>
                        </label>
                    </div>
                </div>

                {/* Master Ingredients List */}
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Name
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Base Unit
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Linked Products
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredIngredients.map((ingredient) => {
                                    const linkedProducts = getLinkedProducts(ingredient.id);
                                    return (
                                        <tr key={ingredient.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{ingredient.name}</div>
                                                    {ingredient.description && (
                                                        <div className="text-sm text-gray-500">{ingredient.description}</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {ingredient.base_unit ? `${ingredient.base_unit.name} (${ingredient.base_unit.abbreviation})` : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                    {linkedProducts.length} product{linkedProducts.length !== 1 ? 's' : ''}
                                                </div>
                                                {linkedProducts.length > 0 && (
                                                    <div className="text-xs text-gray-500">
                                                        {linkedProducts.slice(0, 2).map(p => p.name).join(', ')}
                                                        {linkedProducts.length > 2 && ` +${linkedProducts.length - 2} more`}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${ingredient.is_active
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {ingredient.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => openEditModal(ingredient)}
                                                        className="text-blue-600 hover:text-blue-900"
                                                    >
                                                        <PencilIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setSelectedIngredient(ingredient);
                                                            setProductSearch(ingredient.name);
                                                            setShowLinkModal(true);
                                                        }}
                                                        className="text-green-600 hover:text-green-900"
                                                        title="Link Products"
                                                    >
                                                        <PlusIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {filteredIngredients.length === 0 && (
                        <div className="text-center py-12">
                            <BeakerIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No master ingredients</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {searchTerm
                                    ? 'No master ingredients match your search.'
                                    : 'Get started by creating your first master ingredient.'}
                            </p>
                        </div>
                    )}
                </div>

            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Create Master Ingredient</h3>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                    rows={3}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Base Unit</label>
                                <select
                                    value={formData.base_unit_id}
                                    onChange={(e) => setFormData({ ...formData, base_unit_id: e.target.value })}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                    required
                                >
                                    <option value="">Select a unit...</option>
                                    {['volume', 'weight', 'count', 'length'].map(unitType => {
                                        const unitsOfType = units.filter(unit => unit.unit_type === unitType);
                                        if (unitsOfType.length === 0) return null;

                                        return (
                                            <optgroup key={unitType} label={unitType.charAt(0).toUpperCase() + unitType.slice(1)}>
                                                {unitsOfType.map((unit) => (
                                                    <option key={unit.id} value={unit.id}>
                                                        {unit.name} ({unit.abbreviation})
                                                        {unit.is_base_unit ? ' - base' : ''}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        );
                                    })}
                                </select>
                            </div>
                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        setFormData({ name: '', description: '', base_unit_id: '' });
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {submitting ? 'Creating...' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedIngredient && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Master Ingredient</h3>
                        <form onSubmit={handleEdit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                    rows={3}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Base Unit</label>
                                <select
                                    value={formData.base_unit_id}
                                    onChange={(e) => setFormData({ ...formData, base_unit_id: e.target.value })}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                    required
                                >
                                    <option value="">Select a unit...</option>
                                    {['volume', 'weight', 'count', 'length'].map(unitType => {
                                        const unitsOfType = units.filter(unit => unit.unit_type === unitType);
                                        if (unitsOfType.length === 0) return null;

                                        return (
                                            <optgroup key={unitType} label={unitType.charAt(0).toUpperCase() + unitType.slice(1)}>
                                                {unitsOfType.map((unit) => (
                                                    <option key={unit.id} value={unit.id}>
                                                        {unit.name} ({unit.abbreviation})
                                                        {unit.is_base_unit ? ' - base' : ''}
                                                    </option>
                                                ))}
                                            </optgroup>
                                        );
                                    })}
                                </select>
                            </div>
                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setSelectedIngredient(null);
                                        setFormData({ name: '', description: '', base_unit_id: '' });
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {submitting ? 'Updating...' : 'Update'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Link Products Modal */}
            {showLinkModal && selectedIngredient && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-2/3 max-w-4xl shadow-lg rounded-md bg-white">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Link Products to {selectedIngredient.name}
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Unlinked Products */}
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Available Products</h4>

                                {/* Search Field */}
                                <div className="mb-3">
                                    <div className="relative">
                                        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search products..."
                                            value={productSearch}
                                            onChange={(e) => setProductSearch(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </div>

                                <div className="border border-gray-200 rounded-md max-h-96 overflow-y-auto">
                                    {getUnlinkedProducts().map((product) => (
                                        <div key={product.id} className="p-3 border-b border-gray-100 flex justify-between items-center">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                                {product.manufacturer && (
                                                    <div className="text-xs text-gray-500">{product.manufacturer}</div>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => handleLinkProduct(product.id, selectedIngredient.id)}
                                                className="text-blue-600 hover:text-blue-900 text-sm"
                                            >
                                                Link
                                            </button>
                                        </div>
                                    ))}
                                    {getUnlinkedProducts().length === 0 && (
                                        <div className="p-4 text-center text-gray-500 text-sm">
                                            No unlinked products available
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Linked Products */}
                            <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Linked Products</h4>
                                <div className="border border-gray-200 rounded-md max-h-96 overflow-y-auto">
                                    {getLinkedProducts(selectedIngredient.id).map((product) => (
                                        <div key={product.id} className="p-3 border-b border-gray-100 flex justify-between items-center">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                                {product.manufacturer && (
                                                    <div className="text-xs text-gray-500">{product.manufacturer}</div>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => handleLinkProduct(product.id, '')}
                                                className="text-red-600 hover:text-red-900 text-sm"
                                            >
                                                Unlink
                                            </button>
                                        </div>
                                    ))}
                                    {getLinkedProducts(selectedIngredient.id).length === 0 && (
                                        <div className="p-4 text-center text-gray-500 text-sm">
                                            No products linked yet
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end pt-4">
                            <button
                                onClick={() => {
                                    setShowLinkModal(false);
                                    setSelectedIngredient(null);
                                    setProductSearch('');
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}

export const Route = createFileRoute('/inventory/master-ingredients')({
  component: MasterIngredientsPage,
})
